<script>
    import { scale } from 'svelte/transition';
    import { onDestroy } from 'svelte';
    import { Rive, EventType, Layout, Fit, Alignment } from '@rive-app/canvas';

    import { changeValueOfAnimationText, getPrefixText, RIVE_ANIMATIONS } from '@src/utils/animations.js';
    import { getAnimationParams } from '@src/utils/animations.js';
    import { allowToBet, allowToSkip } from '@src/store';
    import { EventEmitterInstance } from '@root/src/utils/event-emitter';
    import { playAudio } from '@root/src/utils/audio';

    // feature sounds
    // import audio_wheel_appears from '@src/assets/sounds/wheel_appears.mp3';
    // import audio_wheel_spin from '@src/assets/sounds/wheel_spin.mp3';
    // import audio_wheel_choose from '@src/assets/sounds/wheel_coefficient.mp3';
    // import audio_wheel_double from '@src/assets/sounds/double_wheel.mp3';

    export let sectorMultipliers = null;
    export let soundEnabled = false;

    let animation;
    let animationParams;
    let dynamicArrMultipliers = null;
    let dynamicSelectedMultiplier = null;

    let timerShowDuration = null;
    let timer;
    let intervalId;
    let timerChooseSound;
    let flagDoubled = false;

    const clearTextRunInterval = () => {
        if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
        }
    };

    const runAnimation = canvas => {
        if (animation) {
            animation.cleanup();
        }
        const stateMachineName = 'State Machine 1';
        const artBoard = 'crazy_bonus_wheel';

        const spinToSector = 'spin_to_sector';
        const sectorCurrent = 'sector_current';

        const startTrigger = 'start';
        const endTrigger = 'end';
        const bonusTrigger = 'bonus';
        const isFirstSectorDouble = 's1_double';
        const doubleBoolean = 'double';

        const multipliersToRender = sectorMultipliers;

        const textFillSectors = (idx, riveParams, multiplier) => {
            const prefixText = getPrefixText(multiplier);

            if (multiplier === 'DOUBLE' && idx === 1) {
                riveParams[isFirstSectorDouble].value = true; // set text DOUBLE
            }

            changeValueOfAnimationText(animation.artboard, `s${idx}_fill`, `${prefixText}${multiplier}`);
            changeValueOfAnimationText(animation.artboard, `s${idx}_stroke`, `${prefixText}${multiplier}`);
        };

        const setTextRun = (arr, riveParams) => {
            arr?.forEach((multiplier, index) => {
                const idx = ++index;
                textFillSectors(idx, riveParams, multiplier);
            });
        };

        const setTextRunInterval = (arr, riveParams, interval) => {
            let currentIndex = 0;

            const updateText = () => {
                if (currentIndex >= arr.length) {
                    clearInterval(intervalId);
                    intervalId = null;
                    return;
                }

                const multiplier = arr[currentIndex];
                const idx = currentIndex + 1;
                textFillSectors(idx, riveParams, multiplier);
                currentIndex++;
            };

            if (intervalId) {
                clearInterval(intervalId);
            }

            updateText();

            intervalId = setInterval(updateText, interval);
        };

        const setTextWin = choosedMultiplier => {
            // Win center
            const prefixText = getPrefixText(choosedMultiplier);
            const multiplier = choosedMultiplier;

            changeValueOfAnimationText(animation.artboard, `win_fill`, `${prefixText}${multiplier}`);
            changeValueOfAnimationText(animation.artboard, `win_shadow`, `${prefixText}${multiplier}`);

            changeValueOfAnimationText(animation.artboard, `win_double_fill`, `${prefixText}${multiplier}`);
            changeValueOfAnimationText(animation.artboard, `win_double_shadow`, `${prefixText}${multiplier}`);
        };

        animation = new Rive({
            src: RIVE_ANIMATIONS.WHEEL_OF_FORTUNE,
            canvas,
            autoplay: true,
            stateMachines: stateMachineName,
            artboard: artBoard,
            layout: new Layout({
                fit: Fit.Cover,
                alignment: Alignment.Center
            }),
            onLoad: () => {
                animation.resizeDrawingSurfaceToCanvas(1.5);

                animationParams = getAnimationParams(animation, stateMachineName, [
                    spinToSector,
                    sectorCurrent,
                    startTrigger,
                    doubleBoolean,
                    isFirstSectorDouble,
                    endTrigger,
                    bonusTrigger
                ]);

                allowToSkip.set(false);
                allowToBet.set(false);

                if (animation) {
                    EventEmitterInstance.on('top-slot-bonusTrigger', () => {
                        timer = setTimeout(() => {
                            animationParams[bonusTrigger].fire();
                        }, 700);
                    });
                }

                setTextRun(multipliersToRender, animationParams);

                EventEmitterInstance.on('response-bonus-fortune-wheel', response => {
                    allowToBet.set(false);
                    const data = response?.resp;

                    const sectorValueSelected = data.fortune_wheel_result.sector;
                    const sectorIndexSelected = data?.fortune_wheel_result?.sector_index;

                    if (sectorValueSelected === 'DOUBLE') {
                        const sectors = data?.fortune_wheel_params?.sectors;
                        animationParams[doubleBoolean].value = true;
                        const selectedMultiplierIndex = sectorIndexSelected + 1;
                        animationParams[spinToSector].value = selectedMultiplierIndex;
                        animationParams[sectorCurrent].value = sectors.length - selectedMultiplierIndex;
                        flagDoubled = true;
                    } else {
                        animationParams[doubleBoolean].value = false;
                        animationParams[spinToSector].value = sectorIndexSelected + 1;
                    }

                    dynamicArrMultipliers = data?.fortune_wheel_params?.sectors;
                    dynamicSelectedMultiplier = sectorValueSelected;

                    setTextWin(sectorValueSelected);

                    animationParams[startTrigger].fire();
                    timerChooseSound = setTimeout(() => {
                        playAudio('audio_wof_win');
                    }, 9500);
                });
            },
            onStateChange: event => {
                const eventName = event?.data?.[0];
                // console.log('STATE:', eventName);

                if (eventName?.match(/win/)) {
                    timerShowDuration = setTimeout(() => {
                        EventEmitterInstance.emit('promise-end'); // resolve waitEnd
                    }, 1500);
                }
            }
        });

        animation.on(EventType.RiveEvent, event => {
            if (event.data?.name === 'ready') {
                allowToBet.set(true);
            }

            if (event.data?.name === 'bonus' && flagDoubled) {
                const selectedMultiplierIndex = dynamicArrMultipliers.indexOf(dynamicSelectedMultiplier) + 1;

                if (dynamicSelectedMultiplier !== 'DOUBLE') {
                    animationParams[spinToSector].value = selectedMultiplierIndex - 1;
                    animationParams[sectorCurrent].value = selectedMultiplierIndex - 1;
                } else {
                    animationParams[spinToSector].value = selectedMultiplierIndex - 1;
                    animationParams[sectorCurrent].value = 1;
                }

                setTextRunInterval(dynamicArrMultipliers, animationParams, 100);
            }
        });
    };

    onDestroy(() => {
        EventEmitterInstance.removeListener('top-slot-bonusTrigger');
        EventEmitterInstance.removeListener('response-bonus-fortune-wheel');
        EventEmitterInstance.removeListener('promise-end');
        clearTimeout(timerShowDuration);
        clearTimeout(timer);
        clearTimeout(timerChooseSound);
        clearTextRunInterval();
        animation.cleanup();
    });
</script>

<div
    class="absolute left-0 top-0 -mt-7 flex h-full w-full items-center justify-center overflow-hidden s-phone:mt-0"
    out:scale
>
    <canvas id="canvas" class="relative z-[2] w-full bg-transparent" width="500" height="570" use:runAnimation />
</div>
